// src/components/pages/Movimento/CreateOrEdit/modules/useAssinaturasModule.js
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useActionLock } from '../../../../../hooks/useActionLock';
import { useMRTListController } from '../../../../../hooks/useMRTController';

/**
 * Módulo headless de Assinaturas do Movimento
 *
 * Objetivo:
 * - padronizar assinatura (validação usuário/senha) e lista (grid MRT)
 * - suportar "assinar durante o cadastrar" via snapshot no Form.js
 *
 * Injeções esperadas via params:
 * - dados, natureza
 * - APIs: UsuarioAPI (assinarMovimento), MovimentoAPI (deletarAssinatura)
 * - setFlashMessage
 *
 * Observação:
 * - listar/remote fetch fica no Form.js (fetchData), mas o módulo expõe setRemote para padronizar.
 */
export function useAssinaturasModule(params) {
    const { dados, natureza, UsuarioAPI, MovimentoAPI, setFlashMessage, itensModule, operacaoParametros, fetchData, mrt } =
        params;

    const [pendingSignature, setPendingSignature] = useState(null);
    const [pendingExecuted, setPendingExecuted] = useState(false);

    // =========================
    // STATE (grid)
    // =========================
    const [itensAssinatura, setItensAssinatura] = useState([]);
    const [rowCountAssinatura, setRowCountAssinatura] = useState(0);

    const saveLock = useActionLock();
    const removeLock = useActionLock();

    // =========================
    // STATE (draft)
    // =========================
    const [draft, setDraft] = useState({
        id: null,
        uuid: null,

        empresaId: dados?.empresaId ?? null,
        movimentoId: dados?.id ?? null,
        pessoaId: null,

        usuario: '',
        senha: '',

        // aplicação / cargos (mantenha compatível com seu backend)
        compras: false,
        supervisorCompras: false,
        gerenteCompras: false,

        ativo: true,
        funcao: 'Assinar',
        natureza: natureza,
    });

    // =========================
    // HELPERS
    // =========================
    const commitRemote = useCallback((list, totalRowCount) => {
        const safe = Array.isArray(list) ? list : [];
        setItensAssinatura(safe);

        const total = Number(totalRowCount);
        setRowCountAssinatura(Number.isFinite(total) ? total : safe.length);
    }, []);

    const canSign = useMemo(() => {
        const u = (draft?.usuario || '').trim();
        const s = (draft?.senha || '').trim();
        return !!u && !!s;
    }, [draft?.usuario, draft?.senha]);

    // =========================
    // ACTIONS
    // =========================
    const clear = useCallback(() => {
        setDraft((prev) => ({
            ...prev,

            id: null,
            uuid: null,

            empresaId: dados?.empresaId ?? null,
            movimentoId: dados?.id ?? null,
            pessoaId: null,

            usuario: '',
            senha: '',

            compras: false,
            supervisorCompras: false,
            gerenteCompras: false,

            ativo: true,
            funcao: 'Assinar',
            natureza: natureza,
        }));
    }, [dados?.empresaId, dados?.id, natureza]);

    const change = useCallback((e) => {
        const { name, value, checked, type } = e.target;

        if (!name.startsWith('novaAssinatura.')) return;

        const campo = name.split('.')[1];

        setDraft((prev) => ({
            ...prev,
            [campo]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    /**
     * Apaga uma assinatura (tabela filha do movimento).
     * selectedRow: row.original do MRT (ou objeto equivalente)
     */
    const remove = useCallback(
        async (selectedRow) => {
            if (!selectedRow) return { ok: false };

            try {
                const response = await MovimentoAPI.deletarAssinatura(selectedRow);

                const msg = response?.data?.message || 'Assinatura removida';
                const type = response?.data?.type || 'success';

                setFlashMessage(msg, type);

                return { ok: true, response };
            } catch (error) {
                console.error(error);

                const msg = error?.response?.data?.message || 'Erro ao remover assinatura';
                setFlashMessage(msg, 'error');

                return { ok: false, error };
            }
        },
        [MovimentoAPI, setFlashMessage]
    );

    /**
     * Assina o movimento.
     *
     * Pode receber:
     * - snapshot: objeto completo (ideal para pending signature)
     * - ou usa draft interno
     *
     * Regras:
     * - exige movimentoId
     * - chama UsuarioAPI.assinarMovimento (valida + assina)
     */
    const sign = useCallback(
        async (opts = {}) => {
            const { snapshot = null, movimentoIdOverride = null } = opts;

            const payloadBase = snapshot || draft;

            const movimentoId = movimentoIdOverride ?? payloadBase?.movimentoId ?? dados?.id ?? null;

            if (!movimentoId) {
                setFlashMessage('Movimento ainda não foi gravado. Salve o movimento antes de assinar.', 'warning');
                return { ok: false };
            }

            const usuario = (payloadBase?.usuario || '').trim();
            const senha = (payloadBase?.senha || '').trim();

            if (!usuario || !senha) {
                setFlashMessage('Usuário e senha são obrigatórios para assinar.', 'warning');
                return { ok: false };
            }

            const payload = {
                ...payloadBase,
                movimentoId,
                natureza: natureza ?? payloadBase?.natureza,
            };

            try {
                const result = await UsuarioAPI.assinarMovimento(payload);

                const msg = result?.data?.message || result?.message || 'Assinatura processada';
                const type = result?.data?.type || result?.type || 'success';

                setFlashMessage(msg, type);

                clear();

                return { ok: true, result };
            } catch (error) {
                console.error(error);

                if (error?.response?.status === 422) {
                    setFlashMessage('Erro de validação: ' + (error.response.data?.message || ''), 'error');
                } else {
                    setFlashMessage('Ocorreu um erro ao assinar o movimento.', 'error');
                }

                return { ok: false, error };
            }
        },
        [UsuarioAPI, draft, dados?.id, natureza, setFlashMessage, clear]
    );

    /**
     * Assina se já existir movimento; se não existir, agenda para assinar após salvar.
     * OBS: aqui NÃO zeramos a senha no snapshot quando for assinar imediatamente,
     * senão o sign() vai barrar.
     */
    const signOrSchedule = useCallback(async () => {
        if (!dados?.id) {
            const snapshot = { ...draft };
            setPendingSignature(snapshot);
            setPendingExecuted(false);
            setFlashMessage('Assinatura agendada: salve o movimento para concluir.', 'info');
            clear();
            return { scheduled: true };
        }

        const result = await sign({ snapshot: draft, movimentoIdOverride: dados.id });

        // ✅ 1) atualiza a grid de assinatura (é o correto para esse módulo)
        await fetchData('assinatura');

        // ✅ 2) só se usar tabela de preço, re-sincroniza itens
        if (operacaoParametros?.formTabelaOrProduto) {
            const response = await MovimentoAPI.obterMovimentoItemByIdOrUUID({ uuid: dados.uuid });

            const mov = response?.data || response;
            const itens = mov?.movimentoItems || [];

            itensModule?.actions?.setRemote?.(itens, itens.length);
        }

        return { signed: true, result };
    }, [
        draft,
        dados?.id,
        dados?.uuid,
        sign,
        fetchData,
        setFlashMessage,
        clear,
        MovimentoAPI,
        itensModule,
        setPendingSignature,
        setPendingExecuted,
        operacaoParametros,
    ]);

    const refresh = useCallback(async () => {
        if (!dados?.id || !mrt) return;

        return await fetchData('assinatura', mrt);
    }, [dados?.id, mrt, fetchData]);

    // useMRTListController({
    //     mrt,
    //     contextKey: `${dados?.id || 'novo'}-assinatura`,
    //     resolveBoot: () => null,
    //     fetchData: refresh,
    //     contextReady: !!mrt && !!dados?.id,
    // });

    // Executa assinatura pendente assim que o movimento existir (após salvar)
    useEffect(() => {
        if (!dados?.id) return;
        if (!pendingSignature) return;
        if (pendingExecuted) return;

        (async () => {
            setPendingExecuted(true);
            await sign({
                snapshot: pendingSignature,
                movimentoIdOverride: dados.id,
            });
            setPendingSignature(null);
        })();
    }, [dados?.id, pendingSignature, pendingExecuted, sign]);

    const main = {
        state: dados,
    };

    const draftContract = {
        value: draft,
        setValue: setDraft,
        clear,
    };

    const list = {
        items: itensAssinatura,
        rowCount: rowCountAssinatura,
        setItems: setItensAssinatura,
        setRowCount: setRowCountAssinatura,
    };

    const mrtContract = {
        raw: mrt,
        state: {
            columnFilterFns: mrt?.columnFilterFns ?? {},
            columnFilters: mrt?.columnFilters ?? [],
            globalFilter: mrt?.globalFilter ?? '',
            pagination: mrt?.pagination ?? { pageIndex: 0, pageSize: 10 },
            sorting: mrt?.sorting ?? [],
        },
        setters: {
            setColumnFilterFnsMode: mrt?.setColumnFilterFnsMode || mrt?.setColumnFiltersMode,
            setColumnFilters: mrt?.setColumnFilters,
            setGlobalFilter: mrt?.setGlobalFilter,
            setPagination: mrt?.setPagination,
            setSorting: mrt?.setSorting,
        },
        flags: {
            isLoading: false,
            isRefetching: false,
            isError: false,
        },
    };

    const flags = {
        isSaving: saveLock.isLocked,
        isRemoving: removeLock.isLocked,
        canSign,
    };

    const actions = {
        change,
        focus: async () => {},
        blur: async () => {},
        clear,
        refresh,
        submitDraft: async () =>
            await saveLock.run(async () => {
                const result = await signOrSchedule();

                if (dados?.id) {
                    await refresh();
                }

                return result;
            }),

        remove: async (selectedRow) =>
            await removeLock.run(async () => {
                const result = await remove(selectedRow);

                if (result?.ok && dados?.id) {
                    await refresh();
                }

                return result;
            }),

        sign,
        signOrSchedule,

        setRemote: commitRemote,
    };

    const state = {
        draft,
        setDraft,
        itensAssinatura,
        rowCountAssinatura,
        canSign,
        pendingSignature,
        pendingExecuted,
    };

    return {
        key: 'assinatura',

        main,
        draft: draftContract,
        list,
        mrt: mrtContract,
        flags,
        actions,

        services: {
            sign,
            signOrSchedule,
            remove,
            clear,
            change,
            commitRemote,
        },

        state, // compat temporária
    };
}
